import { useState, useEffect } from "react";
import { getPresignedUrlFromApplicationId } from "../service/uploadFileService";
import { FilePreview } from "../../../components/ui/FilePreview";
import Snipper from "@components/ui/Snipper";
import { TabsTrigger, Tabs, TabsList } from "@components/shadcn-ui/tabs";
import { Card } from "@components/shadcn-ui/card";
import { Button } from "@components/shadcn-ui/button";
import { TabsContent } from "@radix-ui/react-tabs";
import { Download } from "lucide-react";
import {DOCUMENT_CATEGORIES, FileCategory} from "./FileCategoryModal";
import { Badge } from "@components/shadcn-ui/badge";

interface Document {
  presignedUrl: string;
  objectKey: string;
}

// Define the structure of documents with dynamic categories
type DocumentsType = Record<string, Document[]>;

interface MobileFileManagerProps {
  applicationId: string;
  documentCategories?: FileCategory[]; // Add this prop
}

export const MobileFileManager = ({
                                    applicationId,
                                    documentCategories = DOCUMENT_CATEGORIES, // Default to all categories if not specified
                                  }: MobileFileManagerProps) => {
    const ALL_FILES = "ALL FILES";
    let sections = [ALL_FILES, ...documentCategories];
  let defaultValue = ALL_FILES;
  if (documentCategories?.length < 2) {
      sections = [...documentCategories]
      if (documentCategories) {
          defaultValue = documentCategories[0];
      }
  }
  const [documents, setDocuments] = useState<DocumentsType>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      setError(null);

      try {
        const { documents } = await getPresignedUrlFromApplicationId(
            applicationId
        );

        setDocuments(documents);
      } catch (err) {
        setError("Failed to fetch files.");
      } finally {
        setLoading(false);
      }
    };

    if (applicationId) {
      fetchFiles();
    }
  }, [applicationId]);

  const allFiles = Object.values(documents).flat();

    return (
        <div className="mt-1 h-full">
            {loading && (
                <div className="flex items-center justify-center gap-2 w-full">
                    <Snipper />
                    <p>Fetching documents...</p>
                </div>
            )}
            {error && <p>{error}</p>}
            {!loading && !error && (
                <Tabs defaultValue={defaultValue} className="w-full">
                    <TabsList className="flex flex-wrap justify-start h-fit mb-2">
                        {sections.map((section) => (
                            <TabsTrigger key={section} value={section}>
                                {section}
                                {(section === ALL_FILES
                                    ? allFiles?.length
                                    : documents[section]?.length) > 0 && (
                                    <Badge className="py-0 px-2 ml-2">
                                        {section === ALL_FILES
                                            ? allFiles?.length || 0
                                            : documents[section]?.length || 0}
                                    </Badge>
                                )}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    {sections.map((section) => {
                        const documents_ =
                            section === ALL_FILES ? allFiles : documents[section] || [];
                        return (
                            <TabsContent key={section} value={section} className="flex flex-col gap-2">
                                {!(documents_.length > 0) ? (
                                    // Changed tr to div
                                    <div className="text-center flex justify-center text-gray-500 p-4">
                                        You have not uploaded any documents in this section.
                                    </div>
                                ) : (
                                    <>
                                        {documents_?.map((doc, index) => (
                                            <div key={doc.objectKey + index} className="flex flex-wrap gap-2">
                                                <Card className="flex flex-col gap-0 p-4 w-full">
                                                    <div className="mb-2 flex justify-between items-center">
                                                        <p className="text-md text-gray-600">
                                                            <div className="text-md font-semibold text-gray-500 border border-gray-300">
                                                                <img
                                                                    src={doc.presignedUrl}
                                                                    alt=""
                                                                    className="max-h-[50px] w-[50px]"
                                                                    onClick={() => {
                                                                        setShowImageModal(true);
                                                                        setModalImage(doc.presignedUrl);
                                                                    }}
                                                                />
                                                            </div>
                                                        </p>
                                                        <div className="flex gap-1">
                                                            <Button>
                                                                <a
                                                                    href={doc.presignedUrl || "#"}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    <Download />
                                                                </a>
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <p className="w-[200px] text-sm text-gray-500 whitespace-nowrap overflow-hidden overflow-ellipsis">
                                                        {doc.objectKey.split("/").pop()}
                                                    </p>
                                                </Card>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </TabsContent>
                        );
                    })}
                </Tabs>
            )}
            <FilePreview
                modalImage={modalImage}
                closeModal={() => {
                    setShowImageModal(false);
                    setModalImage(null);
                }}
                showImageModal={showImageModal}
                setShowImageModal={setShowImageModal}
            />
        </div>
    );
};
